import { type RefObject, useEffect, useState } from "react";

/**
 * Whether an element is narrower than a breakpoint.
 *
 * The element, not the window. Every list on this app sits inside a frame the
 * navigation shares — open the sidebar and the table loses three hundred
 * pixels without the window changing at all — so a layout that reads the
 * viewport is a layout that is wrong exactly when it matters. A media query
 * cannot see this; a `ResizeObserver` on the frame itself can.
 *
 * Starts `false` and corrects on the first measurement: a width of zero is an
 * element that has not been laid out yet, not a narrow one.
 */
export function useNarrow(ref: RefObject<HTMLElement | null>, breakpoint: number): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      setNarrow(width > 0 && width < breakpoint);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, breakpoint]);
  return narrow;
}
